<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue"
import { useContentPreview } from "../composables/useContentPreview"

const props = defineProps({
  labels: { type: Object, default: () => ({}) },
  hostElement: { type: Object, default: null },
})

const offcanvasElement = ref(null)
let offcanvasInstance = null

const {
  isLoading,
  errorMessage,
  content,
  editUrl,
  viewUrl,
  label,
  handlePreviewEvent,
} = useContentPreview(props.labels)

function statusClass(status) {
  return status === "published" ? "bg-success" : "bg-warning"
}

function languageClass(language) {
  return language === "de" ? "bg-info" : "bg-primary"
}

function showOffcanvas() {
  offcanvasInstance?.show()
}

watch(isLoading, (loading) => {
  if (loading) showOffcanvas()
})

watch(content, (value) => {
  if (value) showOffcanvas()
})

watch(errorMessage, (message) => {
  if (message) showOffcanvas()
})

function onPreviewEvent(event) {
  handlePreviewEvent(event)
}

onMounted(() => {
  document.addEventListener("search-autocomplete:preview", onPreviewEvent)

  if (offcanvasElement.value && window.bootstrap?.Offcanvas) {
    offcanvasInstance = new window.bootstrap.Offcanvas(offcanvasElement.value)
  }
})

onUnmounted(() => {
  document.removeEventListener("search-autocomplete:preview", onPreviewEvent)
  offcanvasInstance?.dispose()
})
</script>

<template>
  <div class="content-preview-root">
    <div
      ref="offcanvasElement"
      class="offcanvas offcanvas-end content-preview-offcanvas"
      tabindex="-1"
      id="contentPreviewOffcanvas"
      aria-labelledby="contentPreviewTitle"
    >
      <div class="offcanvas-header">
        <h5 id="contentPreviewTitle" class="offcanvas-title">
          {{ content?.name || "" }}
        </h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="offcanvas"
          :aria-label="label('close', 'Close')"
        />
      </div>

      <div class="offcanvas-body">
        <div v-if="isLoading" class="preview-loading">
          <i class="fa-solid fa-spinner fa-spin" />
          {{ label("loading", "Loading...") }}
        </div>

        <div v-else-if="errorMessage" class="preview-error">
          <i class="fa-solid fa-exclamation-triangle" />
          {{ errorMessage }}
        </div>

        <template v-else-if="content">
          <div class="preview-meta">
            <span class="badge" :class="statusClass(content.status)">{{ content.status }}</span>
            <span class="badge" :class="languageClass(content.language)">
              {{ content.language?.toUpperCase() || "?" }}
            </span>
            <span class="badge bg-secondary">{{ content.type }}</span>
          </div>

          <div v-if="content.books?.length" class="preview-books">
            <span
              v-for="book in content.books"
              :key="book.id || book.name || book"
              class="badge badge-book"
            >{{ book.name || book }}</span>
          </div>

          <div v-if="content.description" class="preview-description">
            <h6>{{ label("description", "Beschreibung") }}</h6>
            <p>{{ content.description }}</p>
          </div>

          <div v-if="content.dataEntries?.length" class="preview-data">
            <h6>{{ label("data", "Daten") }}</h6>
            <dl>
              <div
                v-for="entry in content.dataEntries"
                :key="entry.key"
                class="preview-field"
              >
                <dt>{{ entry.label }}</dt>
                <dd v-html="entry.valueHtml" />
              </div>
            </dl>
          </div>
        </template>
      </div>

      <div class="offcanvas-footer">
        <a :href="editUrl" class="btn btn-primary">
          <i class="fa-solid fa-pen me-1" />
          {{ label("edit", "Edit") }}
        </a>
        <a :href="viewUrl" class="btn btn-outline-light" target="_blank">
          <i class="fa-solid fa-external-link me-1" />
          {{ label("viewFull", "View Full") }}
        </a>
      </div>
    </div>
  </div>
</template>
